import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Alert, Card } from 'react-bootstrap';
import { SectionLoader } from '../../Components/LoadingSpinner';
import { Plus, Filter } from 'lucide-react';
import { useTodos } from '../../hooks/todos';
import { useUserNames } from '../../Helpers/useUserNames';
import { TodoItem } from '../../Components/TodoItem';
import { AddTodoModal } from '../../Components/AddTodoModal';
import { DeleteTodoModal } from '../../Components/DeleteTodoModal';
import TodoFilterModal from './TodoFilterModal';
import './TodosPage.css';

interface TodosListProps {
    userId?: string;
    buildingKey?: string;
    showHeader?: boolean;
    headerTitle?: string;
    headerDescription?: string;
}

// Session storage keys for filter state
const FILTER_KEY_USER = 'todos-filter-show-completed-user';
const FILTER_KEY_BUILDING = 'todos-filter-show-completed-building';

export const TodosList: React.FC<TodosListProps> = ({
    userId,
    buildingKey,
    showHeader = true,
    headerTitle,
    headerDescription
}) => {
    const isUserContext = !!userId;
    const filterKey = isUserContext ? FILTER_KEY_USER : FILTER_KEY_BUILDING;

    // Initialize from session storage
    const getInitialShowCompleted = useCallback(() => {
        const stored = sessionStorage.getItem(filterKey);
        return stored === 'true';
    }, [filterKey]);

    const [showCompleted, setShowCompletedState] = useState(getInitialShowCompleted);

    // Update session storage when filter changes
    const setShowCompleted = useCallback((value: boolean) => {
        setShowCompletedState(value);
        sessionStorage.setItem(filterKey, String(value));
    }, [filterKey]);

    // Re-initialize when context changes
    useEffect(() => {
        setShowCompletedState(getInitialShowCompleted());
    }, [filterKey, getInitialShowCompleted]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
    const [showFilterModal, setShowFilterModal] = useState(false);

    // Get user names for display
    const { userNames, loadUserNames } = useUserNames();

    // Use custom hook for todos management
    const {
        loading,
        error,
        submitting,
        activeTodos,
        completedTodos,
        addTodo,
        toggleTodo,
        updateTodo,
        deleteTodo,
        clearError,
        todos
    } = useTodos({ userId, buildingKey });

    // The names cache starts empty for this component; without loading them
    // every todo read "Skapad av Okänd användare".
    useEffect(() => {
        const uids = Object.values(todos).flatMap(todo => [todo.createdBy, todo.completedBy]);
        const unique = Array.from(new Set(uids.filter((uid): uid is string => !!uid)));
        if (unique.length > 0) loadUserNames(unique);
    }, [todos, loadUserNames]);

    // Memoize displayed todos based on filter
    const displayTodos = useMemo(() =>
        showCompleted ? [...activeTodos, ...completedTodos] : activeTodos,
        [activeTodos, completedTodos, showCompleted]
    );

    // Handlers
    const handleOpenAddModal = () => {
        setShowAddModal(true);
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
    };

    const handleDeleteConfirm = () => {
        if (showDeleteModal) {
            deleteTodo(showDeleteModal);
            setShowDeleteModal(null);
        }
    };

    if (loading) {
        return <SectionLoader />;
    }

    return (
        <div className="todos-content">
            {showHeader && (
                <div className="todos-header-section">
                    <div className="todos-header">
                        <h1 className="page-title">{headerTitle || 'Att göra'}</h1>
                        <div className="todos-header-actions">
                            <Button
                                variant="primary"
                                onClick={handleOpenAddModal}
                            >
                                <Plus size={18} className="me-1" />
                                Lägg till
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowFilterModal(true)}
                            >
                                <Filter size={18} className="me-1" />
                                Filter
                            </Button>
                        </div>
                    </div>
                    {headerDescription && (
                        <p className="todos-header-description">{headerDescription}</p>
                    )}
                </div>
            )}

            {error && (
                <Alert variant="danger" dismissible onClose={clearError} className="mb-3">
                    {error}
                </Alert>
            )}

            {/* Todos list */}
            {displayTodos.length === 0 ? (
                <Card className="todos-empty-card dropshadow">
                    <Card.Body className="todos-empty-card-body">
                        <p className="todos-empty-text">Inga uppgifter att visa</p>
                    </Card.Body>
                </Card>
            ) : (
                <div className="todos-list">
                    {displayTodos.map((todo) => (
                        <TodoItem
                            key={todo.key}
                            todo={todo}
                            userNames={userNames}
                            onToggle={() => toggleTodo(todo.key, todo)}
                            onEdit={(newText) => updateTodo(todo.key, todo, newText)}
                            onDelete={() => setShowDeleteModal(todo.key)}
                            submitting={submitting}
                        />
                    ))}
                </div>
            )}

            {/* Add Todo Modal */}
            <AddTodoModal
                show={showAddModal}
                onHide={handleCloseAddModal}
                onAdd={addTodo}
                submitting={submitting}
            />

            {/* Delete confirmation modal */}
            <DeleteTodoModal
                show={showDeleteModal !== null}
                onHide={() => setShowDeleteModal(null)}
                onConfirm={handleDeleteConfirm}
            />

            {/* Filter Modal */}
            <TodoFilterModal
                show={showFilterModal}
                onHide={() => setShowFilterModal(false)}
                showCompleted={showCompleted}
                onShowCompletedChange={setShowCompleted}
                completedCount={completedTodos.length}
            />
        </div>
    );
};

export default TodosList;
